/**
 * Native cart engine (replaces the liquid-ajax-cart library).
 *
 * Owns all Cart AJAX API calls, serializes them through a queue, and asks
 * Shopify to render the cart sections in the same request (Section Rendering
 * API) so cart components can re-render themselves.
 *
 * Public API:    window.Cart — { state, add, change, update, refresh, addForCheckout, bundleLines }
 * Success event: document 'cart:change'
 *                detail { action, payload, response, cart, previousCart, sections, source }
 *                action is 'add', 'change', 'update', 'refresh', 'gift' for
 *                the engine's own free gift lines (see "free gifts" below), or
 *                'discount' for its bundle codes (see "bundle codes"); analytics
 *                and the add-to-cart UI leave those two alone.
 * Failure event: document 'cart:error'
 *                detail { action, payload, error: { status, message, description }, source }
 *
 * Add-to-cart posts FormData, so /cart/add.js responds with the added line
 * item at the top level — component-data-layer.js and
 * component-cart-notification.js rely on that shape.
 *
 * Wiring is event delegation on markup that already exists:
 *   - form[action*="/cart/add"] submits
 *   - a[href*="/cart/change"] clicks (quantity steppers + remove links)
 *   - .cart-quantity input and cart note textarea changes
 */

const ROOT = window.Shopify?.routes?.root || '/';

function readInitialState() {
  try {
    const el = document.getElementById('cart-data');
    return el ? JSON.parse(el.textContent) : null;
  } catch {
    return null;
  }
}

/* Set while a page is about to leave for checkout: nothing on it will be seen again, so no section is rendered. */
let leaving = false;

/* Section ids of the cart components currently on the page, read from their
   `.shopify-section` ancestors — nothing hardcoded. */
function sectionIds() {
  if (leaving) return [];
  const ids = new Set();
  document.querySelectorAll('cart-drawer, cart-page').forEach((el) => {
    const section = el.closest('.shopify-section');
    if (section) ids.add(section.id.replace('shopify-section-', ''));
  });
  return [...ids];
}

function sectionParams(ids) {
  if (!ids.length) return {};
  return {
    sections: ids.join(','),
    sections_url: window.location.pathname + window.location.search,
  };
}

/* sections/cart-state.liquid: the cart as JSON, with the gift data, rendered in the same request as an add so the
   add's response carries the new cart (and, for Cart.addForCheckout, the gift data). Null when Shopify left it out
   or it can't be read; the caller then fetches /cart.js. */
const CART_STATE_SECTION = 'cart-state';

function readCartState(sections) {
  const html = sections?.[CART_STATE_SECTION];
  if (!html) return null;
  try {
    const json = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-state]')?.textContent;
    return json ? JSON.parse(json) : null;
  } catch {
    return null;
  }
}

function syncCartCount(cart) {
  if (!cart) return;
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = cart.item_count;
  });
  document.querySelectorAll('[data-cart-dot]').forEach((el) => {
    el.classList.toggle('is-visible', cart.item_count > 0);
  });
}

function jsonPost(body) {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  };
}

async function requestJson(url, options) {
  const response = await fetch(url, options);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body.message || response.statusText);
    error.status = response.status;
    error.description = body.description || body.message || 'Something went wrong. Please try again.';
    throw error;
  }
  return body;
}

/* All mutations run through one promise chain so rapid clicks can't race. */
let queue = Promise.resolve();
let pendingJobs = 0;

function enqueue(task) {
  pendingJobs += 1;
  document.documentElement.classList.add('cart-busy');
  const job = queue.then(task).finally(() => {
    pendingJobs -= 1;
    if (pendingJobs === 0) document.documentElement.classList.remove('cart-busy');
  });
  queue = job.catch(() => {});
  return job;
}

function mutate(action, payload, source, runRequest, { withSections = true, refetchCart = action === 'add', keepSections = false } = {}) {
  return enqueue(async () => {
    const previousCart = Cart.state;
    try {
      const ids = withSections ? sectionIds() : [];
      const response = await runRequest(refetchCart ? [...ids, CART_STATE_SECTION] : ids);
      const sections = response.sections ? { ...response.sections } : null;

      if (refetchCart) {
        // Add responses are the added items, not the cart: the cart-state section rendered with the add carries
        // it, so the drawer opens without a second round trip. /cart.js is the fallback.
        Cart.state = readCartState(sections) || (await requestJson(`${ROOT}cart.js`));
        // Kept when the gift data must come from it (no other section is rendered)
        if (sections && !keepSections) delete sections[CART_STATE_SECTION];
      } else {
        const cart = { ...response };
        delete cart.sections;
        Cart.state = cart;
      }

      syncCartCount(Cart.state);
      document.dispatchEvent(
        new CustomEvent('cart:change', {
          detail: { action, payload, response, cart: Cart.state, previousCart, sections, source, leaving },
        })
      );
      return response;
    } catch (error) {
      document.dispatchEvent(
        new CustomEvent('cart:error', {
          detail: {
            action,
            payload,
            error: { status: error.status || 0, message: error.message, description: error.description || error.message },
            source,
          },
        })
      );
      throw error;
    }
  });
}

const Cart = {
  state: readInitialState(),

  add(formData, source) {
    return mutate('add', formData, source, (ids) => {
      const params = sectionParams(ids);
      Object.keys(params).forEach((key) => formData.append(key, params[key]));
      return requestJson(`${ROOT}cart/add.js`, { method: 'POST', body: formData });
    });
  },

  /** payload: { line, quantity } or { id, quantity } */
  change(payload, source) {
    return mutate('change', payload, source, (ids) =>
      requestJson(`${ROOT}cart/change.js`, jsonPost({ ...payload, ...sectionParams(ids) }))
    );
  },

  /** payload: { note } / { attributes } */
  update(payload, source, options) {
    return mutate(
      'update',
      payload,
      source,
      (ids) => requestJson(`${ROOT}cart/update.js`, jsonPost({ ...payload, ...sectionParams(ids) })),
      options
    );
  },

  /**
   * One line per bundle item, each `quantity` times. Each keeps its own
   * subscription plan when the shopper chose "subscribe", and carries
   * `_bundle_parent`: the bundle product's variant, as live's bundle lines do.
   */
  bundleLines(items, parentVariantId, isSubscription, quantity = 1) {
    return items.map((item) => ({
      id: item.id,
      quantity,
      sellingPlan: isSubscription ? item.sellingPlan : null,
      properties: { _bundle_parent: String(parentVariantId) },
    }));
  },

  /** Re-fetches state + section HTML without mutating — used after external
      cart changes (e.g. the discount form does its own requests). */
  refresh() {
    return enqueue(async () => {
      const previousCart = Cart.state;
      const ids = sectionIds();
      Cart.state = await requestJson(`${ROOT}cart.js`);
      const sections = ids.length ? await requestJson(`${ROOT}?sections=${ids.join(',')}`) : null;

      syncCartCount(Cart.state);
      document.dispatchEvent(
        new CustomEvent('cart:change', {
          detail: { action: 'refresh', payload: null, response: Cart.state, cart: Cart.state, previousCart, sections, source: null },
        })
      );
      return Cart.state;
    });
  },

  /**
   * Adds lines for a caller about to leave for checkout (the bundle builder's
   * Recharge lines), and resolves once the gift lines the cart then earns are
   * in. The add's response carries the new cart and its gift data (the
   * cart-state section), and no drawer or cart section is rendered, as the
   * page is leaving.
   * @param {FormData} formData
   * @returns {Promise<void>}
   */
  async addForCheckout(formData) {
    leaving = true;
    try {
      await mutate('add', formData, null, (ids) => {
        formData.append('sections', ids.join(','));
        return requestJson(`${ROOT}cart/add.js`, { method: 'POST', body: formData });
      }, { keepSections: true });
      while (pendingJobs || giftRun) await (giftRun || queue);
    } finally {
      leaving = false;
    }
  },
};

window.Cart = Cart;

/* ----- free gifts ----- */

/**
 * Keeps the free gift lines in step with the cart:
 *
 * - Bundle gifts. A bundle set on the bundle discount in the
 *   ecomexperts-x-piquelife app is complete while each of its products (one is
 *   enough) is in the cart on a subscription, with at least its minimum
 *   quantity of them all together. Unless the bundle's "Also apply when its
 *   products are added separately" is on, they must come from one bundle
 *   product's lines (same `_bundle_parent`); a bundle limited to bundle
 *   products (`parents`) only from one of those. Each complete bundle earns one of
 *   each of its gift variants, as a one-time line with `_free_gift: true` and
 *   `_bundle_gift: true`. Only lines marked `_bundle_gift` are added, trimmed
 *   or removed here.
 * - Threshold gifts. Each reached tier of the cart threshold bar earns one of
 *   each of its gift variants, as a one-time line with `_free_gift: true` and
 *   `_threshold_gift: true`. Which tiers are reached is decided in Liquid
 *   (snippets/component-cart-threshold.liquid), which renders the earned
 *   variants; only lines marked `_threshold_gift` follow that list. A tier
 *   gift already in the cart as another kind of gift is not added twice.
 * - A product's gift (`_gift_parent`, added by featured-product-v2 or the offer
 *   cards) is removed once any variant it names has left the cart.
 *   `_gift_parent` is a comma-separated list of variant ids: the product's
 *   variant, or for a bundle, the bundle's item variants.
 * - A product's own gifts (its `custom.gift_products_any`, and its
 *   `custom.gift_products` while that variant is on a selling plan) follow
 *   each line, wherever it was added: switching a line to one-time removes
 *   its subscription gifts, switching it back adds them again. They go in
 *   with `_gift_parent` set to that variant. A gift a product doesn't list (a
 *   section's buy-button gift) is only removed with its parent.
 * - Each gift variant is in the cart once across the kinds: a product gift
 *   that two products list is added once, and a bundle or threshold gift that
 *   a product gift already supplies is not added again. Only complete bundles
 *   each earn their own.
 * - A bundle, threshold or product gift whose product another gift in the cart
 *   replaces (that gift's `custom.replaces_gifts`, a list of products the data
 *   snippet expands to their variants) is removed, or not added. Two gifts
 *   that replace each other both stay.
 * - Supply-length gifts. A supply card's lines carry its gift as
 *   `_supply_gift`, the next shorter length's as `_supply_gift_short`, and the
 *   lines the card added as `_supply_group` (snippets/component-supply-plans.liquid).
 *   While those lines are all in the cart at their quantity the cart holds one
 *   of the first; once they are there but fewer, one of the second; once one
 *   has gone, neither. They go in as one-time lines with `_free_gift: true`
 *   and `_supply_length_gift: true`, as live's drawer adds them, and
 *   `_gift_parent` set to the line that earned them. Supply lines earn no
 *   product or bundle gifts of their own. Only subscription lines count: a
 *   line switched to one-time in the cart takes its gift with it. The gift
 *   discount prices the gift from the earning product's gift metafields or
 *   from a bundle that lists it, so a card's gift needs one of the two.
 * - Referral gift. On a visit from a ReferralCandy link, the cart holds one of
 *   the data's `referralGift` while it has a paid line, as on live, as a line
 *   with `_free_gift: true` and `_referral_gift: true`; with no paid line it is
 *   removed. Outside such a visit those lines are left alone. The gift is
 *   tagged `Free Product`, so the store's "Free" discount prices it, not the
 *   gift discount.
 *
 * Gifts managed elsewhere (the bundle builder's `_free_gift` lines) are never
 * touched.
 *
 * An add-to-cart that completes a bundle carries its gifts in the same request
 * (appendGifts). After every cart change the rest is planned in one go
 * and sent as at most two requests: one /cart/update.js for every removal and
 * trim, one /cart/add.js for every missing gift. Both run under the 'gift'
 * action.
 *
 * The data comes from snippets/component-bundle-gifts-data.liquid and
 * snippets/component-cart-threshold.liquid, both rendered in the cart drawer
 * and cart page, so every change's sections carry a fresh copy. A page without
 * the threshold data leaves threshold lines alone. The discount app prices the
 * gifts, so a gift this misses is charged, never wrongly free.
 */

const GIFT_PROPERTY = '_free_gift';
const BUNDLE_GIFT_PROPERTY = '_bundle_gift';
const THRESHOLD_GIFT_PROPERTY = '_threshold_gift';
const BUNDLE_GIFT_PROPERTIES = { [GIFT_PROPERTY]: 'true', [BUNDLE_GIFT_PROPERTY]: 'true' };
const THRESHOLD_GIFT_PROPERTIES = { [GIFT_PROPERTY]: 'true', [THRESHOLD_GIFT_PROPERTY]: 'true' };
const SUPPLY_GIFT_PROPERTY = '_supply_length_gift';
const SUPPLY_GIFT_PROPERTIES = { [GIFT_PROPERTY]: 'true', [SUPPLY_GIFT_PROPERTY]: 'true' };
const SUPPLY_GROUP_PROPERTY = '_supply_group';
const REFERRAL_GIFT_PROPERTY = '_referral_gift';
const REFERRAL_GIFT_PROPERTIES = { [GIFT_PROPERTY]: 'true', [REFERRAL_GIFT_PROPERTY]: 'true' };
const MAX_GIFT_PASSES = 5;
const GIFT_DATA_PATTERN = /<script[^>]*data-bundle-gifts[^>]*>([\s\S]*?)<\/script>/;
const THRESHOLD_DATA_PATTERN = /<script[^>]*data-threshold-gifts[^>]*>([\s\S]*?)<\/script>/;

function parseGiftData(json) {
  if (!json) return null;
  try {
    const data = JSON.parse(json);
    return {
      bundles: Array.isArray(data.bundles) ? data.bundles : [],
      allowSeparateItems: data.allowSeparateItems === true,
      replaces: data.replaces || {},
      variantProducts: data.variantProducts || {},
      productGifts: data.productGifts || {},
      referralGift: data.referralGift ? String(data.referralGift) : null,
    };
  } catch (error) {
    console.error('Cart: invalid bundle gift data', error);
    return null;
  }
}

/* The gift variants the reached threshold tiers earn, once per tier. */
function parseThresholdGifts(json) {
  if (!json) return null;
  try {
    const data = JSON.parse(json);
    return Array.isArray(data.gifts) ? data.gifts.map(String) : null;
  } catch (error) {
    console.error('Cart: invalid threshold gift data', error);
    return null;
  }
}

/* The data in a cart change's section HTML, if any section carries it. */
function dataFromSections(sections, pattern, parse) {
  for (const html of Object.values(sections || {})) {
    const data = parse(html?.match(pattern)?.[1]);
    if (data) return data;
  }
  return null;
}

/* A visit from a ReferralCandy link (`utm_campaign=referralcandy_<id>`): on the landing URL, then from the UTM
   parameters an app keeps for the session as `as-customer-trigger-data`, as live's drawer read them. */
function isReferralVisit() {
  let campaign = new URLSearchParams(window.location.search).get('utm_campaign');
  if (!campaign) {
    try {
      campaign = JSON.parse(sessionStorage.getItem('as-customer-trigger-data'))?.utm_campaign;
    } catch {
      campaign = null;
    }
  }
  return String(campaign || '').includes('referralcandy_');
}

let giftData = parseGiftData(document.querySelector('[data-bundle-gifts]')?.textContent);
let thresholdData = parseThresholdGifts(document.querySelector('[data-threshold-gifts]')?.textContent);

/**
 * The variants to drop: each gift in the cart drops the variants of the
 * products it replaces, unless one of them also replaces it.
 */
function droppedGifts(present, replaces) {
  const dropped = new Set();

  for (const gift of present) {
    for (const drop of (replaces[gift] || []).map(String)) {
      const mutual = (replaces[drop] || []).map(String).includes(gift);
      if (drop !== gift && present.has(drop) && !mutual) dropped.add(drop);
    }
  }

  return dropped;
}

/**
 * Whether the items still hold the lines `line`'s supply card added (its `_supply_group`), at the same supply length,
 * on a subscription as `line` is or one-time as `line` is: each at its quantity, or with `full` false, at least one of
 * each.
 */
function supplyGroupInCart(items, line, full = true) {
  const group = line.properties?.[SUPPLY_GROUP_PROPERTY];
  if (!group) return true;
  const sameCard = (item) =>
    Boolean(item.selling_plan_allocation) === Boolean(line.selling_plan_allocation) &&
    String(item.properties?._supply_length || '') === String(line.properties._supply_length || '');
  return String(group)
    .split(',')
    .every((entry) => {
      const [variant, quantity] = entry.split(':').map((part) => part.trim());
      let held = 0;
      for (const item of items) {
        if (String(item.variant_id) === variant && sameCard(item)) held += item.quantity;
      }
      return held >= (full ? parseInt(quantity, 10) || 1 : 1);
    });
}

/**
 * Each place a bundle is complete among these subscription lines, one entry
 * per place, as in the app's shared/bundle-rules.js `bundleMatches`. A bundle
 * whose "Also apply when its products are added separately" is on
 * (`allowSeparateItems`) is completed from all the lines, at most once; any
 * other only from one bundle product's lines (same `_bundle_parent`), once per
 * bundle product that holds it. It needs every one of its products (one is
 * enough), and at least its `minQuantity` units of them all together, or of
 * each with `minPerProduct`. A bundle saved before each had its own setting
 * takes the discount's old one for every bundle (`data.allowSeparateItems`).
 *
 * A bundle limited to bundle products (`parents`, their variant ids) only
 * counts the lines those bundle products added, whatever its separate-items
 * setting, so two bundle products with the same children can differ. That
 * limit is for gifts only: `ignoreParents` drops it for the bundle codes,
 * which follow the bundle discount's percentage rule. The other way round, a
 * bundle limited to selling plans (`plans`, their ids) is for the % and code
 * only: `withPlans` counts just the lines on one of them, as live's supply
 * length codes follow the delivery length.
 *
 * Each place is `{ bundle, lines }`: the lines of the bundle's products it was
 * completed from.
 */
function completeBundles(subscribed, data, { ignoreParents = false, withPlans = false } = {}) {
  const byParent = new Map();
  for (const item of subscribed) {
    const parent = item.properties?._bundle_parent;
    if (parent) byParent.set(String(parent), [...(byParent.get(String(parent)) || []), item]);
  }
  const heldLines = (bundle, lines) => {
    const products = (bundle.products || []).map(String);
    const plans = withPlans ? (bundle.plans || []).map(String) : [];
    return lines.filter(
      (item) =>
        products.includes(String(item.product_id)) &&
        (!plans.length || plans.includes(String(item.selling_plan_allocation?.selling_plan?.id)))
    );
  };
  const holdsBundle = (bundle, held) => {
    const products = [...new Set((bundle.products || []).map(String))];
    const units = new Map();
    for (const item of held) units.set(String(item.product_id), (units.get(String(item.product_id)) || 0) + item.quantity);
    const minQuantity = Math.max(0, Math.floor(Number(bundle.minQuantity) || 0));
    if (!products.length || !products.every((product) => units.has(product))) return false;
    if (bundle.minPerProduct === true) return products.every((product) => units.get(product) >= minQuantity);
    return [...units.values()].reduce((sum, quantity) => sum + quantity, 0) >= minQuantity;
  };
  const bundleGroups = (bundle) => {
    const parents = ignoreParents ? [] : (bundle.parents || []).map(String);
    if (parents.length) return [...byParent].filter(([parent]) => parents.includes(parent)).map(([, lines]) => lines);

    const separate = typeof bundle.allowSeparateItems === 'boolean' ? bundle.allowSeparateItems : data.allowSeparateItems;
    return separate ? [subscribed] : [...byParent.values()];
  };

  return data.bundles.flatMap((bundle) =>
    bundleGroups(bundle)
      .map((lines) => heldLines(bundle, lines))
      .filter((held) => holdsBundle(bundle, held))
      .map((held) => ({ bundle, lines: held }))
  );
}

/**
 * The matches whose percentage applies, as the app's shared/bundle-rules.js
 * `applyingMatches`: percentages are tiers, from the highest down, and a match
 * applies unless a higher tier already took one of its lines. Matches in one
 * tier all apply, even on shared lines. Gift-only bundles (0%) take no part.
 */
function applyingMatches(matches) {
  const tiers = new Map();
  for (const match of matches) {
    const percentage = Number(match.bundle.percentage) || 0;
    if (percentage > 0) tiers.set(percentage, [...(tiers.get(percentage) || []), match]);
  }

  const taken = new Set();
  const applying = [];
  for (const [, tier] of [...tiers].sort(([a], [b]) => b - a)) {
    const free = tier.filter((match) => match.lines.every((line) => !taken.has(line)));
    applying.push(...free);
    for (const match of free) for (const line of match.lines) taken.add(line);
  }
  return applying;
}

/**
 * The applying matches less any that would discount a line twice, as the
 * app's `exclusiveMatches`: a match is left out when one of its lines belongs
 * to an earlier match with another code (no code is the app's own %). Earlier
 * is a higher tier, then the order on the bundle discount's page.
 */
function exclusiveMatches(applying) {
  const owners = new Map();
  return applying.filter((match) => {
    const code = codeKey(match.bundle.code);
    if (match.lines.some((line) => owners.has(line) && owners.get(line) !== code)) return false;
    for (const line of match.lines) owners.set(line, code);
    return true;
  });
}

/**
 * Everything that brings the gift lines in line with these items, checked
 * across the whole cart first: the new quantity of every line to remove or
 * trim (by line key) and every gift to add, with its line properties.
 * Lines not in the cart yet have no `key`. Without `thresholds` (a page with no
 * threshold data) threshold lines are left alone, like any other gift, and
 * without `referral` (not a ReferralCandy visit) referral lines are.
 */
function giftPlan(items, data, thresholds, referral = false) {
  const updates = {};
  const adds = [];
  const variant = (item) => String(item.variant_id);
  const count = (variants) => {
    const counts = new Map();
    for (const gift of variants) counts.set(gift, (counts.get(gift) || 0) + 1);
    return counts;
  };

  // Supply lines earn their card's gift instead of a bundle's, as on live
  const subscribed = items.filter(
    (item) => item.selling_plan_allocation && item.product_id && !item.properties?._supply_gift
  );
  const earned = count(completeBundles(subscribed, data).flatMap(({ bundle }) => (bundle.gifts || []).map(String)));
  const thresholdEarned = count(thresholds || []);

  const gifts = items.filter((item) => item.properties?.[GIFT_PROPERTY] === 'true' && !item.selling_plan_allocation);
  const bundleGifts = gifts.filter((item) => item.properties[BUNDLE_GIFT_PROPERTY] === 'true');
  const thresholdGifts = thresholds
    ? gifts.filter((item) => item.properties[THRESHOLD_GIFT_PROPERTY] === 'true' && !bundleGifts.includes(item))
    : [];
  const supplyGifts = gifts.filter((item) => item.properties[SUPPLY_GIFT_PROPERTY] === 'true' && !bundleGifts.includes(item));
  const referralGifts = gifts.filter((item) => item.properties[REFERRAL_GIFT_PROPERTY] === 'true');
  // As live: one while any line other than the gift itself is paid for
  const referralGift = referral ? data.referralGift : null;
  const referralEarned = new Map(
    referralGift && items.some((item) => variant(item) !== referralGift && item.final_line_price > 0) ? [[referralGift, 1]] : []
  );
  const productGifts = gifts.filter(
    (item) =>
      item.properties._gift_parent && !bundleGifts.includes(item) && !thresholdGifts.includes(item) && !supplyGifts.includes(item)
  );

  /* A supply card's gift: its own while the lines it added are all in at their quantity, its shorter length's once
     they are fewer, none once one has gone */
  // Only while the line is a subscription: switched to one-time in the cart, it earns no supply gift
  const supplyLines = items.filter((item) => !gifts.includes(item) && item.properties?._supply_gift && item.selling_plan_allocation);
  const supplyEarned = new Map();
  const supplyParents = new Map();
  for (const item of supplyLines) {
    let gift = '';
    if (supplyGroupInCart(items, item)) gift = item.properties._supply_gift;
    else if (supplyGroupInCart(items, item, false)) gift = item.properties._supply_gift_short || '';
    if (!gift) continue;
    supplyEarned.set(String(gift), 1);
    if (!supplyParents.has(String(gift))) supplyParents.set(String(gift), variant(item));
  }
  /* The gift goes in with `_gift_parent` set to the line that earned it, so the
     gift discount can price a gift that line's product lists (B·T Fountain's
     Quiver, in its custom.gift_products); a gift the product doesn't list is
     priced by a bundle on the bundle discount (the 3-month Premium Starter Kit) */
  const supplyGiftProperties = (gift) => ({ ...SUPPLY_GIFT_PROPERTIES, _gift_parent: supplyParents.get(gift) });

  // Supply lines earn no product gifts, so a product gift that came with one goes with it
  const productParents = items.filter((item) => !gifts.includes(item) && !supplyLines.includes(item));
  const parentInCart = (parent) =>
    parent.split(',').every((id) => productParents.some((item) => variant(item) === id.trim()));
  const orphans = productGifts.filter((item) => !parentInCart(String(item.properties._gift_parent)));
  const keptProductGifts = productGifts.filter((item) => !orphans.includes(item));

  /* A product's own gifts (data.productGifts, by its variant): `any` with any
     purchase, `subscription` while a line of that variant is on a selling plan.
     Only gifts on those lists are managed; a section's own gift is left alone. */
  const parentLines = productParents;
  const productGiftLists = (parent) => {
    const lists = data.productGifts[parent];
    if (!lists || !parentLines.some((item) => variant(item) === parent)) return null;

    const subscribed = parentLines.some((item) => variant(item) === parent && item.selling_plan_allocation);
    const any = (lists.any || []).map(String);
    const subscription = (lists.subscription || []).map(String);
    return { listed: new Set([...any, ...subscription]), wanted: new Set(subscribed ? [...any, ...subscription] : any) };
  };
  const unearnedProductGifts = keptProductGifts.filter((item) => {
    const lists = productGiftLists(String(item.properties._gift_parent));
    return lists && lists.listed.has(variant(item)) && !lists.wanted.has(variant(item));
  });
  const earnedProductGifts = keptProductGifts.filter((item) => !unearnedProductGifts.includes(item));

  const otherGifts = gifts.filter(
    (item) =>
      !bundleGifts.includes(item) &&
      !thresholdGifts.includes(item) &&
      !productGifts.includes(item) &&
      !supplyGifts.includes(item) &&
      !referralGifts.includes(item)
  );

  /* Each variant is in the cart as one `_free_gift` line, whatever earned it.
     A product gift is not added while another `_free_gift` line holds its
     variant (an earned bundle, threshold or supply gift, or a gift managed elsewhere),
     and two products listing the same gift add it once: the second parent's
     copy is added only once the first leaves. A copy beside a line managed
     elsewhere, which is never touched, is removed. */
  const heldElsewhere = new Set([
    ...otherGifts.map(variant),
    ...bundleGifts.map(variant).filter((gift) => earned.has(gift)),
    ...thresholdGifts.map(variant).filter((gift) => thresholdEarned.has(gift)),
    ...supplyGifts.map(variant).filter((gift) => supplyEarned.has(gift)),
  ]);
  const otherGiftVariants = new Set(otherGifts.map(variant));
  const productGiftVariants = new Set();
  const duplicateProductGifts = earnedProductGifts.filter((item) => {
    if (productGiftVariants.has(variant(item)) || otherGiftVariants.has(variant(item))) return true;
    productGiftVariants.add(variant(item));
    return false;
  });
  const missingProductGifts = [];
  for (const parent of new Set(parentLines.map(variant))) {
    for (const gift of productGiftLists(parent)?.wanted || []) {
      if (productGiftVariants.has(gift) || heldElsewhere.has(gift)) continue;
      productGiftVariants.add(gift);
      missingProductGifts.push({ gift, parent });
    }
  }
  const elsewhere = new Set(
    [...earnedProductGifts, ...otherGifts]
      .map(variant)
      .concat([...earned.keys()], [...supplyEarned.keys()], [...referralEarned.keys()], missingProductGifts.map(({ gift }) => gift))
  );
  const present = new Set([...elsewhere, ...thresholdEarned.keys()]);
  const dropped = droppedGifts(present, data.replaces);

  const removedProductGifts = [
    ...orphans,
    ...unearnedProductGifts,
    ...duplicateProductGifts,
    ...earnedProductGifts.filter((gift) => dropped.has(variant(gift))),
  ];
  for (const item of removedProductGifts) {
    if (item.key) updates[item.key] = 0;
  }
  for (const { gift, parent } of missingProductGifts) {
    if (!dropped.has(gift)) adds.push({ id: Number(gift), quantity: 1, properties: { [GIFT_PROPERTY]: 'true', _gift_parent: parent } });
  }

  /* Adds or trims one kind of engine-owned gift line to what the cart earns.
     `properties` are the new lines' properties, or a function of the gift
     variant that returns them. */
  const settle = (lines, wanted, properties, skip = new Set()) => {
    for (const gift of new Set([...lines.map(variant), ...wanted.keys()])) {
      const quantity = dropped.has(gift) || skip.has(gift) ? 0 : wanted.get(gift) || 0;
      const giftLines = lines.filter((item) => variant(item) === gift);
      let extra = giftLines.reduce((total, item) => total + item.quantity, 0) - quantity;

      if (extra < 0) {
        adds.push({ id: Number(gift), quantity: -extra, properties: typeof properties === 'function' ? properties(gift) : properties });
        continue;
      }

      for (const line of [...giftLines].reverse()) {
        if (extra <= 0 || !line.key) break;
        const removed = Math.min(line.quantity, extra);
        updates[line.key] = line.quantity - removed;
        extra -= removed;
      }
    }
  };

  // Nor a bundle gift whose variant a product gift's `_free_gift` line, or one managed elsewhere, already holds
  settle(bundleGifts, earned, BUNDLE_GIFT_PROPERTIES, new Set([...productGiftVariants, ...otherGiftVariants]));
  // Nor a supply gift whose variant one of those, or an earned bundle gift, already holds
  settle(supplyGifts, supplyEarned, supplyGiftProperties, new Set([...productGiftVariants, ...otherGiftVariants, ...earned.keys()]));
  settle(thresholdGifts, thresholdEarned, THRESHOLD_GIFT_PROPERTIES, elsewhere);
  // Nor a referral gift whose variant another gift line holds, or will
  if (referral) {
    settle(
      referralGifts,
      referralEarned,
      REFERRAL_GIFT_PROPERTIES,
      new Set([...productGiftVariants, ...otherGiftVariants, ...earned.keys(), ...supplyEarned.keys(), ...thresholdEarned.keys()])
    );
  }

  return { updates, adds };
}

/**
 * The lines an add-to-cart form posts: either one `id` line or `items[n]`.
 */
function formLines(formData) {
  if (formData.has('id')) {
    return [{ id: formData.get('id'), quantity: formData.get('quantity'), sellingPlan: formData.get('selling_plan'), properties: {} }];
  }

  const lines = [];
  for (const [key, value] of formData.entries()) {
    const match = key.match(/^items\[(\d+)\]\[(id|quantity|selling_plan|properties)\](?:\[(.+)\])?$/);
    if (!match) continue;

    const line = (lines[match[1]] ||= { properties: {} });
    if (match[2] === 'properties') line.properties[match[3]] = value;
    else line[match[2] === 'selling_plan' ? 'sellingPlan' : match[2]] = value;
  }
  return lines.filter((line) => line?.id);
}

/**
 * Rewrites a single-line form as `items[0]`, so more lines can follow.
 */
function toItems(formData) {
  if (!formData.has('id')) return;

  const properties = [...formData.entries()].filter(([key]) => /^properties\[.+\]$/.test(key));
  const [line] = formLines(formData);
  for (const key of ['id', 'quantity', 'selling_plan']) formData.delete(key);
  for (const [key] of properties) formData.delete(key);

  formData.append('items[0][id]', line.id);
  formData.append('items[0][quantity]', line.quantity || 1);
  if (line.sellingPlan) formData.append('items[0][selling_plan]', line.sellingPlan);
  for (const [key, value] of properties) formData.append(`items[0][properties]${key.slice('properties'.length)}`, value);
}

/** The gift lists a form's component-product-gifts renders, or null. */
function readProductGifts(form) {
  const json = form.querySelector('[data-product-gifts]')?.textContent;
  if (!json) return null;
  try {
    return JSON.parse(json);
  } catch (error) {
    console.error('Cart: invalid product gift data', error);
    return null;
  }
}

/**
 * Adds the gifts an add-to-cart earns to the same request: the product's own
 * gifts (`productGifts`, from the form) and the bundle gifts it completes. A
 * gift a `_free_gift` line already holds is not added. The new lines' products
 * come from the bundle data's variant map; a variant it doesn't know leaves
 * the bundle gift to the reconcile after the add.
 */
function appendGifts(formData, productGifts = null) {
  if (!giftData || !Cart.state?.items) return;
  // A single-product add's own gifts (the form's component-product-gifts), planned as if the cart listed them
  const lineGifts = productGifts && formData.has('id') ? { [formData.get('id')]: productGifts } : {};
  if (!giftData.bundles.length && !Object.keys(lineGifts).length) return;

  const adding = formLines(formData).map((line) => ({
    variant_id: line.id,
    product_id: giftData.variantProducts[line.id],
    quantity: parseInt(line.quantity, 10) || 1,
    selling_plan_allocation: line.sellingPlan ? {} : null,
    properties: line.properties,
  }));
  // Tiers are decided from the priced cart, which only exists after the add
  const data = { ...giftData, productGifts: { ...giftData.productGifts, ...lineGifts } };
  const { adds } = giftPlan([...Cart.state.items, ...adding], data, null);
  if (!adds.length) return;

  toItems(formData);
  let index = formLines(formData).length;
  for (const gift of adds) {
    const prefix = `items[${index}]`;
    formData.append(`${prefix}[id]`, gift.id);
    formData.append(`${prefix}[quantity]`, gift.quantity);
    for (const [name, value] of Object.entries(gift.properties)) formData.append(`${prefix}[properties][${name}]`, value);
    index += 1;
  }
}

/**
 * Sends a plan: every removal and trim in one /cart/update.js, then every
 * missing gift in one /cart/add.js.
 */
async function applyGiftPlan({ updates, adds }) {
  if (Object.keys(updates).length) {
    await mutate('gift', { updates }, null, (ids) => requestJson(`${ROOT}cart/update.js`, jsonPost({ updates, ...sectionParams(ids) })));
  }

  if (adds.length) {
    await mutate(
      'gift',
      { items: adds },
      null,
      (ids) => requestJson(`${ROOT}cart/add.js`, jsonPost({ items: adds, ...sectionParams(ids) })),
      { refetchCart: true }
    );
  }
}

/* ----- bundle codes ----- */

/**
 * The cart's new discount codes (comma-separated, as /cart/update.js takes
 * them), or null when it already holds the right ones.
 *
 * A bundle on the bundle discount can name a Shopify discount code (`code`) in
 * place of its extra %, because Recharge carries a code used at checkout into
 * the subscription's renewals, which it can't do for the discount app's %. While
 * a bundle's percentage would apply, its code is applied; once no such bundle
 * names it, it is removed. Codes no bundle names, a shopper's or a creator's,
 * are kept. This follows the bundle discount's percentage rule, as in the
 * app's shared/bundle-rules.js `earnedCodes`: every subscription line counts,
 * supply lines too (the 3-month B·T Fountain's code), a bundle's `parents`
 * don't limit it, and the bundle's percentage (what its code gives) ranks it
 * in the tiers, so overlapping bundles never stack two codes (the Skin + Hair
 * Longevity Protocol's code, not the Radiant Skin Duo's inside it). The bundle
 * discount refuses a bundle's code on any other cart, so typing one in does
 * nothing on its own.
 */
function bundleCodes(cart, data) {
  const named = new Set(data.bundles.map((bundle) => codeKey(bundle.code)).filter(Boolean));
  if (!named.size) return null;

  const earned = earnedBundleCodes(cart.items, data);
  const current = cart.discount_codes.map((entry) => entry.code);
  const kept = current.filter((code) => !named.has(codeKey(code)) || earned.has(codeKey(code)));
  const missing = [...earned].filter(([code]) => !current.some((each) => codeKey(each) === code)).map(([, code]) => code);
  if (kept.length === current.length && !missing.length) return null;
  return [...kept, ...missing].join(',');
}

/* The codes of the bundles whose percentage applies, as entered on the bundle discount, by codeKey. */
function earnedBundleCodes(items, data) {
  const subscribed = items.filter((item) => item.selling_plan_allocation && item.product_id);
  const earned = new Map();
  for (const { bundle } of exclusiveMatches(applyingMatches(completeBundles(subscribed, data, { ignoreParents: true, withPlans: true })))) {
    if (codeKey(bundle.code)) earned.set(codeKey(bundle.code), String(bundle.code).trim());
  }
  return earned;
}

/* Codes compare ignoring case, as Shopify's do. */
function codeKey(code) {
  return String(code || '').trim().toUpperCase();
}

/**
 * Brings the cart's discount codes in step with its bundles (see bundleCodes),
 * under the 'discount' action. Resolves true when it changed them.
 */
async function syncBundleCodes() {
  if (!giftData.bundles.some((bundle) => bundle.code)) return false;

  let cart = Cart.state;
  /* Liquid's `cart | json`, the state after an add or a page load, has no
     `discount_codes`, so /cart.js is read for them. Only when a bundle is
     complete, though: otherwise the only change could be dropping a stray
     bundle code, which checkout refuses anyway and the next change drops. */
  if (!Array.isArray(cart.discount_codes)) {
    if (!earnedBundleCodes(cart.items, giftData).size) return false;
    // Queued, so the cart stays busy (dimmed) until the code is in
    cart = await enqueue(() => requestJson(`${ROOT}cart.js`));
  }
  const discount = bundleCodes(cart, giftData);
  if (discount === null) return false;

  await mutate('discount', { discount }, null, (ids) => requestJson(`${ROOT}cart/update.js`, jsonPost({ discount, ...sectionParams(ids) })));
  return true;
}

let giftRun = null;

/* A change made mid-run updates Cart.state and giftData, which the next pass
   reads, so a call while running can simply return. A pass usually settles
   everything; another is needed only when a newly added gift replaces one
   already in the cart. The bundle codes go first: their price can move a
   threshold tier, so planning the gifts after them keeps a tier gift from
   coming in and going straight out again. */
function reconcileGifts() {
  if (giftRun || !giftData || !Cart.state?.items) return;

  giftRun = (async () => {
    try {
      for (let pass = 0; pass < MAX_GIFT_PASSES; pass += 1) {
        if (await syncBundleCodes()) continue;
        const plan = giftPlan(Cart.state.items, giftData, thresholdData, isReferralVisit());
        if (!Object.keys(plan.updates).length && !plan.adds.length) return;
        await applyGiftPlan(plan);
      }
      console.warn('Cart: gift lines did not settle');
    } catch (error) {
      // A sold-out gift can't be added; the gift discount still prices the cart right
      console.warn('Cart: could not update the gift lines or bundle codes:', error.message);
    }
  })().finally(() => {
    giftRun = null;
  });
}

document.addEventListener('cart:change', (event) => {
  const { sections } = event.detail;
  giftData = dataFromSections(sections, GIFT_DATA_PATTERN, parseGiftData) || giftData;
  thresholdData = dataFromSections(sections, THRESHOLD_DATA_PATTERN, parseThresholdGifts) || thresholdData;
  reconcileGifts();
});

syncCartCount(Cart.state);

/* Back from checkout (or any page), the browser shows this page from its back/forward or HTTP cache, with the cart
   as it was when the page was first loaded: read it again. */
window.addEventListener('pageshow', (event) => {
  const navigation = performance.getEntriesByType('navigation')[0];
  if (event.persisted || navigation?.type === 'back_forward') Cart.refresh().catch(() => {});
});
reconcileGifts();
if (!Cart.state) {
  requestJson(`${ROOT}cart.js`)
    .then((cart) => {
      Cart.state = cart;
      syncCartCount(cart);
      reconcileGifts();
    })
    .catch(() => {});
}

/* ----- add-to-cart forms ----- */

/**
 * A bundle product is never added itself. Its form carries the bundle's items
 * (snippets/component-bundle-items.liquid), and the single product line is
 * rewritten as one `items[n]` line per item. A form whose own script already
 * rewrote the add (the product page, which also adds free gifts) has no `id`
 * left, so it is left alone.
 */
function expandBundle(form, formData) {
  const data = form.querySelector('[data-bundle-items]');
  if (!data || !formData.has('id')) return;

  let items;
  try {
    items = JSON.parse(data.textContent);
  } catch (error) {
    console.error('Cart: invalid bundle data', error);
    return;
  }
  if (!items.length) return;

  const lines = Cart.bundleLines(
    items,
    formData.get('id'),
    Boolean(formData.get('selling_plan')),
    parseInt(formData.get('quantity'), 10) || 1
  );

  for (const key of ['id', 'quantity', 'selling_plan']) formData.delete(key);

  for (const [index, line] of lines.entries()) {
    const prefix = `items[${index}]`;
    formData.append(`${prefix}[id]`, line.id);
    formData.append(`${prefix}[quantity]`, line.quantity);
    if (line.sellingPlan) formData.append(`${prefix}[selling_plan]`, line.sellingPlan);
    for (const [name, value] of Object.entries(line.properties)) {
      formData.append(`${prefix}[properties][${name}]`, value);
    }
  }
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (!(form.getAttribute('action') || '').includes('/cart/add')) return;
  // The form's own handler took the submit (the product card's Buy Box opens instead of adding)
  if (event.defaultPrevented) return;
  event.preventDefault();

  const button = form.querySelector('button[name="add"], [type="submit"]');
  const spinner = button?.querySelector('.loading__spinner');
  const errorEl = form.querySelector('.form-error');
  if (errorEl) errorEl.textContent = '';
  // A form can opt out of the disabled button (data-keep-enabled): double submits are ignored instead
  const keepEnabled = form.hasAttribute('data-keep-enabled');
  if (keepEnabled && form.dataset.adding) return;
  if (keepEnabled) form.dataset.adding = 'true';
  if (button && !keepEnabled) button.disabled = true;
  spinner?.classList.remove('hidden');

  const formData = new FormData(form);
  expandBundle(form, formData);
  appendGifts(formData, readProductGifts(form));

  Cart.add(formData, form)
    .catch((error) => {
      if (errorEl) errorEl.textContent = error.description;
      // A 422 can still partially add (max available) — re-sync the UI
      Cart.refresh();
    })
    .finally(() => {
      if (button) button.disabled = false;
      delete form.dataset.adding;
      spinner?.classList.add('hidden');
    });
});

/* ----- line quantity changes (steppers, remove links, typed input) ----- */

const lineDebounce = new Map();

/* `/cart/change` links can target the line by 1-based index (`line=`) or by
   line item key (`id=`) — accept both. Returns null for anything else so the
   click falls back to native navigation. */
function lineTarget(link) {
  const params = new URL(link.href, window.location.origin).searchParams;
  const line = parseInt(params.get('line'), 10);
  if (line) return { line };
  const id = params.get('id');
  if (id) return { id };
  return null;
}

function changeLine(target, quantity, source) {
  Cart.change({ ...target, quantity }, source).catch((error) => {
    const item = source.closest('.cart-item');
    const errorEl = item?.querySelector('.cart-item__error');
    if (errorEl) errorEl.textContent = error.description;
    const input = item?.querySelector('.cart-quantity input');
    if (input) input.value = input.defaultValue;
  });
}

/* Collapses rapid +/- clicks into one request with the final quantity. */
function queueLineChange(target, quantity, source) {
  const key = target.line || target.id;
  clearTimeout(lineDebounce.get(key));
  lineDebounce.set(
    key,
    setTimeout(() => {
      lineDebounce.delete(key);
      changeLine(target, quantity, source);
    }, 250)
  );
}

document.addEventListener('click', (event) => {
  // The remove row's icon sits beside the link, not inside it — treat a click
  // anywhere in the row as a click on its link.
  const removeRow = event.target.closest('.cart-item__remove');
  const link =
    event.target.closest('a[href*="/cart/change"]') ||
    removeRow?.querySelector('a[href*="/cart/change"]');
  if (!link) return;

  const target = lineTarget(link);
  if (!target) return;
  event.preventDefault();

  const params = new URL(link.href, window.location.origin).searchParams;
  const stepper = link.closest('.cart-quantity');
  if (!stepper) {
    // remove link — href already carries quantity=0
    changeLine(target, parseInt(params.get('quantity'), 10) || 0, link);
    return;
  }

  // Step from the input's current value, not the href, so quick successive
  // clicks accumulate correctly; defaultValue is the server-rendered quantity.
  const input = stepper.querySelector('input');
  const direction = parseInt(params.get('quantity'), 10) >= parseInt(input.defaultValue, 10) ? 1 : -1;
  const quantity = Math.max(0, (parseInt(input.value, 10) || 0) + direction);
  input.value = quantity;
  queueLineChange(target, quantity, link);
});

document.addEventListener('change', (event) => {
  const target = event.target;

  /* The cart drawer and cart page use a native select carrying its own line
     reference, so it needs no sibling link to read the line from. One change is
     one value, so it is not debounced the way the steppers are.

     It addresses the line by its stable key rather than its 1-based position:
     mutations are queued, so a position captured when the markup was rendered
     can point at a different line by the time the request is sent — remove line
     1 and 2 quickly and the second request would otherwise hit what was line 3. */
  if (target.matches('select[data-cart-quantity]')) {
    const key = target.dataset.key;
    const line = parseInt(target.dataset.line, 10);
    const lineRef = key ? { id: key } : line ? { line } : null;
    if (!lineRef) return;

    const errorEl = target.closest('.cart-drawer__item, .cart-page__item')?.querySelector('[data-cart-error]');
    if (errorEl) errorEl.textContent = '';

    const quantity = Math.max(0, parseInt(target.value, 10) || 0);
    Cart.change({ ...lineRef, quantity }, target).catch((error) => {
      const original = target.querySelector('option[selected]');
      if (original) target.value = original.value;
      /* Say why, where the shopper is looking. cart:error carries the same
         message for anything that wants to announce it. */
      if (errorEl) errorEl.textContent = error.description;
    });
    return;
  }

  if (target.matches('.cart-quantity input')) {
    const link = target.closest('.cart-quantity')?.querySelector('a[href*="/cart/change"]');
    if (!link) return;
    const lineRef = lineTarget(link);
    if (!lineRef) return;
    const quantity = Math.max(0, parseInt(target.value, 10) || 0);
    target.value = quantity;
    queueLineChange(lineRef, quantity, target);
    return;
  }

  if (target.matches('cart-drawer textarea[name="note"], cart-page textarea[name="note"]')) {
    Cart.update({ note: target.value }, target, { withSections: false }).catch((error) => {
      console.warn('Cart note update failed:', error.message);
    });
  }
});
